import React, { useState, useEffect } from "react";
import {
  Box,
  Image,
  Text,
  VStack,
  Heading,
  SimpleGrid,
  useColorMode,
  Link as ChakraLink,
  HStack,
  Badge,
  useToast,
  Flex,
  Input,
  Button,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";

import axiosInstance from "../../helpers/axiosInstance"; // Adjust the path according to your project structure
import { formatDate } from "../../helpers/utils";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import Translate from "../../components/LanguageTranslation/Translate";
import BackToTopButton from "../common/BackToTopButton";
import BlogBanner from "./BlogBanner";
import { useTranslation } from "../../components/LanguageTranslation/TranslationContext";
import InstagramFeed from "./InstagramFeed";

import darkLogo from "../../assets/images/Newlogo-black.png";
import { Helmet } from "react-helmet-async";

const Blog = () => {
  const { colorMode } = useColorMode();
  const toast = useToast();
  const { useTranslate } = useTranslation();
  const [blogs, setBlogs] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({}); // Moved filter management to Blog component
  const placeholderText = useTranslate("Enter email");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // console.log("selectedFilters", selectedFilters);

  const constructImageUrl = (filename) => {
    if (!filename) return null;

    // Replace backslashes with forward slashes and remove the leading "./"
    const cleanPath = filename.replace(/\\/g, "/").replace(/^\.\//, "");

    const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";
    const url = `${baseUrl}/${cleanPath}`;
    // console.log("Constructed URL:", url);
    return url;
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const response = await axiosInstance.get("/blogs/blogs");
      // console.log("Got response:", response.data);
      if (response.data.success) {
        const blogsWithProcessedImages = response.data.data.map((blog) => ({
          ...blog,
          image_list: blog.image_list.map((image) =>
            image.startsWith("http") ? image : constructImageUrl(image)
          ),
        }));
        setBlogs(blogsWithProcessedImages);
      } else {
        console.error("Failed to fetch blogs:", response.data.message);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error.message);
    }
  };

  // Filter blogs based on selected filters
  const filteredBlogs = blogs.filter((blog) =>
    Object.keys(selectedFilters).every((category) =>
      selectedFilters[category]?.some((value) =>
        blog.category.some(
          (cat) =>
            cat.category_name === category &&
            cat.category_value_list.includes(value)
        )
      )
    )
  );

  // Sort blogs by creation_date (most recent first)
  const sortedBlogs = [...filteredBlogs].sort((a, b) => {
    const dateA = new Date(a.creation_date);
    const dateB = new Date(b.creation_date);
    return dateB - dateA;
  });

  const handleSubscribe = async () => {
    if (!email) {
      console.error("Email is required");
      return;
    }

    setIsLoading(true); // Start loading
    try {
      const response = await axiosInstance.post("/newsletter/", { 
        email, 
        is_manual: true,
        date: new Date().toISOString()
      });
      if (response.status === 201) {
        setEmail(""); // Clear the email input after successful submission
      } else {
        console.error("Subscription failed");
      }
    } catch (error) {
      console.error("Error subscribing to newsletter:", error);
    } finally {
      setIsLoading(false); // Stop loading
    }
  };

  return (
    <>
      <Helmet>
        <title>
          Outdoor Adventures, Travel Tips &amp; More&nbsp;| NOMAD360 Blogs
        </title>
        <meta
          name="description"
          content="Discover outdoor travel tips, adventure guides, and expert insights on the NOMAD360 blog. Stay inspired with our latest articles on outdoor adventures and more."
        />
        <meta
          name="description:mobile"
          content="Get inspired with NOMAD360’s blog. Outdoor travel tips, adventure guides, and expert insights for your next journey."
        />
      </Helmet>

      <Box
        bg={colorMode === "light" ? "white" : "gray.800"}
        position="absolute"
        top={0}
        left={0}
        width="100%"
        zIndex="2"
      >
        {/* Add Navbar at the top */}
        <Navbar
          bgColor="white"
          linkColor="black"
          styling="outline"
          logoSrc={darkLogo}
          userProfileBgColor="transparaent"
          languageCodeColor="black"
        />
        {/* BlogBanner with selectedFilters and setSelectedFilters as props */}
        <Box pb={{ base: 4, md: 16 }} pt={{ base: 4, md: 0 }}>
          <BlogBanner
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
          />
        </Box>
        {/* Blog Content */}
        <Flex justify="center" width="100%">
          <Heading
            as="h1"
            fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
            fontWeight="bold"
            mt={7}
            mb={7}
            textAlign="center"
            fontFamily="Poppins, sans-serif"
          >
            <Translate text="Featured Posts" />
          </Heading>
        </Flex>

        <Flex justify="center" width="100%">
          <Box
            display="flex"
            transition="margin 0.2s ease-in-out"
            pl={{ base: 4, md: 16 }}
            pr={{ base: 4, md: 16 }}
            mb={8}
            fontFamily="Poppins, sans-serif"
            maxWidth={{ base: "1200px", md: "1200px", lg: "1200px", xl: "90%" }}
            width="100%"
          >
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3, xl: 4 }}
              spacing={10}
              mx="auto"
            >
              {sortedBlogs.map((blog) => (
                <Box
                  as={Link}
                  to={`/blogs/${blog._id}/${blog.slug}`}
                  key={blog._id}
                  cursor="pointer"
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                  display="flex"
                  flexDirection="column"
                  bg={colorMode === "light" ? "white" : "gray.700"}
                >
                  <Image
                    src={blog.image_list[0]}
                    alt={blog.seo_title}
                    objectFit="cover"
                    height={{
                      base: "200px",
                      md: "200px",
                      lg: "200px",
                      xl: "300px",
                    }}
                    w={{ base: "400px", md: "400px", lg: "400px", xl: "800px" }}
                  />
                  <Box p={4} flex="1" display="flex" flexDirection="column">
                    <VStack align="start" spacing={2} flex="1">
                      <HStack spacing={2} mb={2}>
                        <Badge
                          bg="black"
                          variant="solid"
                          borderRadius="md"
                          textTransform="none"
                          p={3}
                          pt={1}
                          pb={1}
                        >
                          {blog.category.length > 0
                            ? blog.category[0].category_value_list.join(", ")
                            : "General"}
                        </Badge>
                        <Text fontSize="sm" color="gray.500" mb={0}>
                          {blog.creation_date
                            ? formatDate(blog.creation_date)
                            : "N/A"}
                        </Text>
                      </HStack>
                      <Heading
                        as="h2"
                        fontSize={{
                          base: "18px",
                          md: "18px",
                          lg: "18px",
                          xl: "24px",
                        }}
                        noOfLines={1}
                        width="100%"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        mb={0}
                      >
                        <Translate text={blog.seo_title} />
                      </Heading>
                      <Text
                        noOfLines={2}
                        fontSize={{ base: "sm", md: "sm", lg: "sm", xl: "md" }}
                        color={colorMode === "light" ? "gray.700" : "gray.400"}
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: "2",
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        mb={0}
                      >
                        <Translate text={blog.meta_description} />
                      </Text>
                    </VStack>
                    <ChakraLink
                      as={Link}
                      to={`/blogs/${blog._id}/${blog.slug}`}
                      fontSize={{
                        base: "16px",
                        md: "16px",
                        lg: "16px",
                        xl: "md",
                      }}
                      color="#286328"
                      fontWeight="bold"
                      alignSelf="flex-start"
                      mt={2}
                    >
                      <Translate text="Read more" />
                    </ChakraLink>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </Flex>

        <Box
          mt={12}
          // mb={8}
          p={8}
          pb
          bg="#F7F7F7"
          borderRadius="lg"
          textAlign="center"
        >
          <Heading as="h2" size="lg" mb={4}>
            <Translate text="Join the Green & Wild Community!" />
          </Heading>
          <Text
            mb={6}
            w={{ base: "100%", sm: "100%", md: "50%" }}
            mx="auto"
            textAlign="center"
          >
            <Translate
              text="Subscribe to NOMAD360' newsletter for personalized travel tips,
            epic outdoor experiences, and exclusive offers. Connect with fellow
            explorers and start planning your next adventure today!"
            />
          </Text>
          <HStack
            maxW="500px"
            mx="auto"
            p={2}
            borderRadius="md"
            bg="white"
            boxShadow="sm"
            spacing={4}
          >
            <Input
              type="email"
              placeholder={placeholderText}
              variant="unstyled"
              px={4}
              flex="1"
              value={email}
              onChange={(e) => setEmail(e.target.value)} // Bind email state
            />
            <Button
              bg="black"
              color="white"
              // borderRadius="full"
              px={6}
              _hover={{
                transform: "scale(1.1)",
                // border: "2px solid #FFC719",
                // borderColor: "#FFC719",
                bg: "black",
              }}
              isLoading={isLoading} // Show spinner while loading
              onClick={handleSubscribe} // Call the subscribe function
            >
              <Translate text="Subscribe" />
            </Button>
          </HStack>
        </Box>
        <InstagramFeed />

        <Footer />
        <BackToTopButton />
      </Box>
    </>
  );
};

export default Blog;
